import PropTypes from 'prop-types';
import React from 'react';
import { locationShape, routerShape } from 'react-router/lib/PropTypes';
import _ from 'lodash';

import t from '../../lib/I18n';
import { PointStages, JourneyPlannerLayout, BreakPoints } from '../../constants/AppConstants';
import PointFlow from '../../flows/PointFlow';
import SearchActions from '../../actions/SearchActions';
import JourneyNotificationActions from '../../actions/JourneyNotificationActions';
import SmartcardStore from '../../stores/SmartcardStore';
import PreviousSearchesStore from '../../stores/PreviousSearchesStore';
import DimensionsStore from '../../stores/DimensionsStore';
import User from '../../records/User';
import Search from '../../records/Search';
import Order from '../../records/Order';
import Smartcard from '../../records/customer/Smartcard';
import connectToStores from '../../helpers/connectToStores';
import FeatureFlagStore from '../../stores/FeatureFlagStore';
import TocOnlyStore from '../../stores/TocOnlyStore';
import TrackingActions from "../../actions/TrackingActions";
import SearchLeftColumn from '../search/LeftColumn';
import JourneyDetailsForm from './../search/JourneyDetailsForm';
import JourneyNotificationsModal from '../search/JourneyNotificationsModal';
import ResponsiveRender from '../ResponsiveRender';
import StationFinder from '../stationFinder';
import StationFinderOpenStreetMaps from '../stationFinder/OpenStreetMaps';
import SearchFooter from '../search/Footer';
import NREReferralFrame from '../nre/ReferralFrame';
import FeatureFlag from '../general/FeatureFlag';
import config from '../../config';
import serviceUtils from '../../utils/serviceUtils';
import OrderStore from '../../stores/OrderStore';
import SearchWithoutMap from './SearchWithoutMap';

export class PointSearch extends React.PureComponent {

    static propTypes = {
        user: PropTypes.instanceOf(User).isRequired,
        search: PropTypes.instanceOf(Search).isRequired,
        isMobileWidth: PropTypes.bool.isRequired,
        location: locationShape,
        smartcard: PropTypes.instanceOf(Smartcard),
        enableTocOnlySearch: PropTypes.bool.isRequired,
        hasFooterPoweredByOn: PropTypes.bool,
        currentOrder: PropTypes.instanceOf(Order)
    };

    static defaultProps = {
        smartcard: null,
        location: null
    };

    static contextTypes = {
        router: routerShape
    };

    state = {
        loading: false,
        search: this.props.search,
        searchErrors: this.props.search.getValidationErrors(),
        showJourneyNotificationsModal: false,
        tocOnlyAvailability: false,
        searchAttempted: false
    };

    componentDidMount() {
        this._checkTocOnlyAvailability(this.props.search);
    }

    _handleSearchChange = (search) => {
        const searchErrors = search.getValidationErrors();
        this.setState({ search, searchErrors });
        this._checkTocOnlyAvailability(search);
    };

    _handleStationChange = (stations) => {
        const search = this.state.search
            .set('origin', stations.origin)
            .set('destination', stations.destination)
            .set('via', stations.via)
            .set('avoid', stations.avoid);

        this._handleSearchChange(search);
    };

    _getStations () {
        const search = this.state.search;

        return {
            'origin': search.origin,
            'destination': search.destination,
            'via': search.via,
            'avoid': search.avoid
        };
    }

    _handleStationFinderChange = (type, value) => {
        const stations = { ...this._getStations(), [type]: value };

        this._handleStationChange(stations);
    }

    _handleContinueSearch = async () => {
        this._handleJourneyNotificationsModalClose();
        JourneyNotificationActions.dismissMessages();

        let { search } = this.state;
        const { user, isMobileWidth, currentOrder } = this.props;

        this.setState({loading: true});

        try {
            search = search.set('realTime', false);
            if (isMobileWidth) {
                search = search.setTargetLayout(JourneyPlannerLayout.LIST);
            }

            const hasTicketWithPromo = currentOrder && currentOrder.hasPromoCode();

            search = search.set('orderId', hasTicketWithPromo ? currentOrder.id : null);

            const response = await SearchActions.search({search, user});
            PointFlow.moveToNext(PointStages.SEARCH, { search }, this.context.router).then(() => {
                serviceUtils.handleDifferentDaySearchResult({ search, response });
            });
        } catch (e) {
            this.setState({ loading: false });
            throw e;
        }
    };

    _handleJourneyNotificationsModalClose = () => {
        if (this.state.showJourneyNotificationsModal) {
            this.setState({ showJourneyNotificationsModal: false });
        }
    };

    _handleSearch = async () => {
        const { search } = this.state;

        this.setState({ searchAttempted: true });

        TrackingActions.widgetSearchClick({ name: 'search' });

        const searchErrors = search.getValidationErrors();
        if (!searchErrors.isEmpty()) {
            this.setState({searchErrors});
            return;
        }

        try {
            this.setState({loading: true});
            const { messages } = await JourneyNotificationActions.fetchMessages(search);
            if (messages.size > 0) {
                this.setState({ loading: false, showJourneyNotificationsModal: true });
            } else {
                this._handleContinueSearch();
            }
        } catch (e) {
            this.setState({ loading: false });
            throw e;
        }
    };

    _checkTocOnlyAvailability = (search) => {
        if (this.props.enableTocOnlySearch && search.origin.id && search.destination.id) {
            let tocOnlyAvailability = TocOnlyStore.getAvailability(search);
            if (!tocOnlyAvailability) {
                this.setState({ tocOnlyAvailability: false });
                SearchActions.checkTocOnlyRoute(search)
                    .then((result) => {
                        this.setState({ tocOnlyAvailability: result });
                    });
            } else {
                this.setState({ tocOnlyAvailability: tocOnlyAvailability.get('result') });
            }
        }
    };

    performSearch = (url) => {
        this.context.router.push(url);
    }

    renderMap = () => {
        const { search } = this.state;
        const shouldUseOpenStreetMaps = config.get('enableOpenMaps');

        const MapComponent = shouldUseOpenStreetMaps ? StationFinderOpenStreetMaps : StationFinder;
        return React.createElement(MapComponent, {
            className: "right-col",
            isMobile: false,
            currentSearch: search,
            onChange: this._handleStationFinderChange,
            origin: search.origin,
            destination: search.destination
        });
    }

    render () {
        const { smartcard, user, hasFooterPoweredByOn } = this.props;
        const { search, searchAttempted, searchErrors, loading, showJourneyNotificationsModal, tocOnlyAvailability } = this.state;
        const smartcardChecked = search.smartcard ? _.result(smartcard, 'canLoadProduct') : true;
        const enableMap = FeatureFlagStore.get('enableMap');

        return (
            <div>
                {enableMap ? (
                    <React.Fragment>
                        <SearchLeftColumn loading={ loading } title={ t('pageTitles.point-search') }>
                            <JourneyDetailsForm
                                enableStationFinder
                                search={ search }
                                searchAttempted={ searchAttempted }
                                user={ user }
                                handleSearchChange={ this._handleSearchChange }
                                tocSpecificSelectorVisible={ tocOnlyAvailability }/>
                        </SearchLeftColumn>

                        <ResponsiveRender minWidth={ BreakPoints.TABLET_PORTRAIT_WIDTH }>
                            {this.renderMap()}
                        </ResponsiveRender>
                        <SearchFooter
                            disabled={ loading || !smartcardChecked || (searchAttempted && !searchErrors.isEmpty()) }
                            onSearch={ this._handleSearch }
                            searchAttempted={ searchAttempted }
                            type={ 'point' }
                            hasFooterPoweredByOn={ hasFooterPoweredByOn }
                        />
                    </React.Fragment>
                ) : (
                    <SearchWithoutMap user={user} performSearch={this.performSearch} />
                )}
                <JourneyNotificationsModal
                    show={ showJourneyNotificationsModal }
                    onClose={ this._handleJourneyNotificationsModalClose }
                    onContinue={ this._handleContinueSearch }
                />

                <FeatureFlag name="enableNREHandoffLogging">
                    <NREReferralFrame location={ location } />
                </FeatureFlag>
            </div>
        );
    }
}

function getStateFromStores ({ location }) {
    const smartcard = SmartcardStore.get('smartcard');
    const isMobileWidth = DimensionsStore.isMobileWidth();
    const enableTocOnlySearch = FeatureFlagStore.get('enableTocOnlySearch');
    const hasFooterPoweredByOn = FeatureFlagStore.get('hasFooterPoweredByOn');
    const currentOrder = OrderStore.getCurrent();

    // for some reason when you amend a trip search request pollutes PreviousSearchesStore
    // and isAmending stays true
    let search = PreviousSearchesStore.get('point').set('tocSpecific', false).refreshDates(smartcard).refreshAmending();

    if (location.query.smartcard && location.query.smartcard === 'yes' && smartcard && smartcard.canSearchForProducts()) {
        search = search.set('smartcard', true);
    }

    return {
        isMobileWidth, smartcard, search, enableTocOnlySearch, hasFooterPoweredByOn, currentOrder
    };
}

export default connectToStores([SmartcardStore, DimensionsStore, FeatureFlagStore, OrderStore], getStateFromStores)(PointSearch);



// WEBPACK FOOTER //
// ./src/components/point/Search.js